import JSZip from 'jszip';

/**
 * Minimal OOXML (.xlsx) writer built on jszip, which is already a dependency.
 *
 * Produces a real workbook — not a CSV or an HTML table with an Excel MIME type —
 * so Excel opens it without a "the file format doesn't match the extension"
 * warning and keeps number/percent formatting.
 *
 * Deliberately narrow: inline strings (no sharedStrings table), one fixed style
 * table, no formulas. Enough for tabular analytics exports; reach for a real
 * library if you ever need charts, merged data regions, or multiple fonts.
 */

export type XlsxCellValue = string | number | null | undefined;

export type XlsxColumnType = 'text' | 'number' | 'decimal' | 'percent';

export interface XlsxColumn {
  header: string;
  /** Approximate character width. Defaults to 12. */
  width?: number;
  /** Drives the number format. `percent` expects a FRACTION (0.7818 → 78.18%). */
  type?: XlsxColumnType;
}

export interface XlsxSheet {
  /** Excel caps sheet names at 31 chars and forbids []:*?/\ — both are handled. */
  name: string;
  columns: XlsxColumn[];
  rows: XlsxCellValue[][];
  /** Appended after `rows`, rendered bold on a tinted fill. */
  totalRow?: XlsxCellValue[];
  /** Free-text lines written above the header — title, active filters, source. */
  titleLines?: string[];
}

// ── style table indices (must match buildStyles below) ───────────────────────
const S_TITLE = 1;
const S_SUBTITLE = 2;
const S_HEADER = 3;
const BODY_STYLE: Record<XlsxColumnType, number> = { text: 4, number: 5, decimal: 7, percent: 6 };
const TOTAL_STYLE: Record<XlsxColumnType, number> = { text: 8, number: 9, decimal: 11, percent: 10 };

const escapeXml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
    // Excel rejects most C0 control characters outright — stripping them is the point.
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');

export const columnLetter = (index: number): string => {
  let n = index + 1;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
};

const safeSheetName = (name: string, fallback: string): string => {
  const cleaned = name.replace(/[[\]:*?/\\]/g, ' ').trim().slice(0, 31);
  return cleaned || fallback;
};

const cellXml = (ref: string, value: XlsxCellValue, style: number, type: XlsxColumnType): string => {
  if (value === null || value === undefined || value === '') {
    return `<c r="${ref}" s="${style}"/>`;
  }
  if (type !== 'text' && typeof value === 'number' && Number.isFinite(value)) {
    return `<c r="${ref}" s="${style}"><v>${value}</v></c>`;
  }
  return `<c r="${ref}" s="${style}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(String(value))}</t></is></c>`;
};

const buildSheetXml = (sheet: XlsxSheet): string => {
  const { columns, rows, totalRow, titleLines = [] } = sheet;
  const types = columns.map((c) => c.type ?? 'text');
  const lastCol = columnLetter(Math.max(columns.length - 1, 0));

  const cols = columns
    .map((c, i) => `<col min="${i + 1}" max="${i + 1}" width="${c.width ?? 12}" customWidth="1"/>`)
    .join('');

  const parts: string[] = [];
  let r = 0;

  titleLines.forEach((line, idx) => {
    r += 1;
    parts.push(
      `<row r="${r}" ht="${idx === 0 ? 20 : 14}" customHeight="1">` +
        cellXml(`A${r}`, line, idx === 0 ? S_TITLE : S_SUBTITLE, 'text') +
        `</row>`,
    );
  });
  if (titleLines.length) r += 1; // spacer row

  const headerRow = r + 1;
  r = headerRow;
  parts.push(
    `<row r="${r}" ht="30" customHeight="1">` +
      columns.map((c, i) => cellXml(`${columnLetter(i)}${r}`, c.header, S_HEADER, 'text')).join('') +
      `</row>`,
  );

  rows.forEach((row) => {
    r += 1;
    parts.push(
      `<row r="${r}">` +
        columns
          .map((_, i) => cellXml(`${columnLetter(i)}${r}`, row[i], BODY_STYLE[types[i]], types[i]))
          .join('') +
        `</row>`,
    );
  });

  if (totalRow) {
    r += 1;
    parts.push(
      `<row r="${r}">` +
        columns
          .map((_, i) => cellXml(`${columnLetter(i)}${r}`, totalRow[i], TOTAL_STYLE[types[i]], types[i]))
          .join('') +
        `</row>`,
    );
  }

  const lastDataRow = totalRow ? r - 1 : r;
  // Freeze the header plus the first column so long school names stay visible.
  const freeze =
    `<sheetView showGridLines="0" workbookViewId="0">` +
    `<pane xSplit="1" ySplit="${headerRow}" topLeftCell="B${headerRow + 1}" activePane="bottomRight" state="frozen"/>` +
    `</sheetView>`;

  const autoFilter =
    lastDataRow > headerRow ? `<autoFilter ref="A${headerRow}:${lastCol}${lastDataRow}"/>` : '';

  return (
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    `<sheetViews>${freeze}</sheetViews>` +
    `<cols>${cols}</cols>` +
    `<sheetData>${parts.join('')}</sheetData>` +
    autoFilter +
    `</worksheet>`
  );
};

const buildStyles = (): string =>
  `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
  `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
  `<numFmts count="3">` +
  `<numFmt numFmtId="164" formatCode="0.00%"/>` +
  `<numFmt numFmtId="165" formatCode="#,##0;(#,##0);&quot;-&quot;"/>` +
  `<numFmt numFmtId="166" formatCode="0.00"/>` +
  `</numFmts>` +
  `<fonts count="5">` +
  `<font><sz val="10"/><name val="Arial"/></font>` +
  `<font><b/><sz val="10"/><color rgb="FFFFFFFF"/><name val="Arial"/></font>` +
  `<font><b/><sz val="10"/><name val="Arial"/></font>` +
  `<font><b/><sz val="14"/><color rgb="FF1F3864"/><name val="Arial"/></font>` +
  `<font><i/><sz val="9"/><color rgb="FF595959"/><name val="Arial"/></font>` +
  `</fonts>` +
  `<fills count="4">` +
  `<fill><patternFill patternType="none"/></fill>` +
  `<fill><patternFill patternType="gray125"/></fill>` +
  `<fill><patternFill patternType="solid"><fgColor rgb="FF1F3864"/><bgColor indexed="64"/></patternFill></fill>` +
  `<fill><patternFill patternType="solid"><fgColor rgb="FFFFF2CC"/><bgColor indexed="64"/></patternFill></fill>` +
  `</fills>` +
  `<borders count="2">` +
  `<border><left/><right/><top/><bottom/><diagonal/></border>` +
  `<border>` +
  `<left style="thin"><color rgb="FFBFBFBF"/></left><right style="thin"><color rgb="FFBFBFBF"/></right>` +
  `<top style="thin"><color rgb="FFBFBFBF"/></top><bottom style="thin"><color rgb="FFBFBFBF"/></bottom>` +
  `<diagonal/></border>` +
  `</borders>` +
  `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
  `<cellXfs count="12">` +
  `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>` +
  `<xf numFmtId="0" fontId="3" fillId="0" borderId="0" xfId="0" applyFont="1"/>` +
  `<xf numFmtId="0" fontId="4" fillId="0" borderId="0" xfId="0" applyFont="1"/>` +
  `<xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>` +
  `<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1"/>` +
  `<xf numFmtId="165" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center"/></xf>` +
  `<xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center"/></xf>` +
  `<xf numFmtId="166" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center"/></xf>` +
  `<xf numFmtId="0" fontId="2" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>` +
  `<xf numFmtId="165" fontId="2" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center"/></xf>` +
  `<xf numFmtId="164" fontId="2" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center"/></xf>` +
  `<xf numFmtId="166" fontId="2" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center"/></xf>` +
  `</cellXfs>` +
  `<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>` +
  `</styleSheet>`;

/** Builds the workbook and returns it as a Blob — useful for tests or previews. */
export const buildXlsxBlob = async (sheets: XlsxSheet[]): Promise<Blob> => {
  if (!sheets.length) throw new Error('buildXlsxBlob requires at least one sheet');

  const names = sheets.map((s, i) => safeSheetName(s.name, `Sheet${i + 1}`));
  const zip = new JSZip();

  zip.file(
    '[Content_Types].xml',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
      `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
      `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
      `<Default Extension="xml" ContentType="application/xml"/>` +
      `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
      `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>` +
      sheets
        .map(
          (_, i) =>
            `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
        )
        .join('') +
      `</Types>`,
  );

  zip.file(
    '_rels/.rels',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
      `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
      `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>` +
      `</Relationships>`,
  );

  zip.file(
    'xl/workbook.xml',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
      `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ` +
      `xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
      `<sheets>` +
      names
        .map((n, i) => `<sheet name="${escapeXml(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
        .join('') +
      `</sheets></workbook>`,
  );

  zip.file(
    'xl/_rels/workbook.xml.rels',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
      `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
      sheets
        .map(
          (_, i) =>
            `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
        )
        .join('') +
      `<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
      `</Relationships>`,
  );

  zip.file('xl/styles.xml', buildStyles());
  sheets.forEach((sheet, i) => zip.file(`xl/worksheets/sheet${i + 1}.xml`, buildSheetXml(sheet)));

  return zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    compression: 'DEFLATE',
  });
};

/** Builds the workbook and triggers a browser download. */
export const downloadXlsx = async (fileName: string, sheets: XlsxSheet[]): Promise<void> => {
  const blob = await buildXlsxBlob(sheets);
  const name = fileName.toLowerCase().endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick so Safari has committed the navigation.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/** `My Region / Report` → `my-region-report`, for filenames. */
export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'export';
